<template><div><h1 id="chromeos-recovery-usb" tabindex="-1"><a class="header-anchor" href="#chromeos-recovery-usb"><span>ChromeOS Recovery USB</span></a></h1>
<div class="hint-container caution">
<p class="hint-container-title">WARNING</p>
<ul>
<li>Creating a ChromeOS Recovery USB will <strong>wipe all data</strong> on the USB device</li>
<li>The recovery process will wipe <strong>all data</strong> on the internal drive</li>
</ul>
</div>
<p>This guide covers creating and using a ChromeOS Recovery USB to reinstall ChromeOS on your device.</p>
<h2 id="when-you-need-this" tabindex="-1"><a class="header-anchor" href="#when-you-need-this"><span>When You Need This</span></a></h2>
<ul>
<li>After restoring stock firmware via the <RouteLink to="/docs/fwscript.html">Firmware Utility Script</RouteLink></li>
<li>When ChromeOS is corrupted and won't boot</li>
<li>When preparing to sell or return your device</li>
</ul>
<div class="hint-container tip">
<p class="hint-container-title">NOTE</p>
<p>If you only flashed RW_LEGACY firmware, you don't need to restore stock firmware first. Simply perform a ChromeOS USB recovery and exit Developer Mode.</p>
</div>
<h2 id="prerequisites" tabindex="-1"><a class="header-anchor" href="#prerequisites"><span>Prerequisites</span></a></h2>
<ul>
<li>A PC or laptop to create the recovery USB</li>
<li>A USB drive or SD card (8GB minimum)</li>
<li>Your device's board name or model identifier</li>
</ul>
<h2 id="part-1-creating-the-recovery-usb" tabindex="-1"><a class="header-anchor" href="#part-1-creating-the-recovery-usb"><span>Part 1: Creating the Recovery USB</span></a></h2>
<p>There are two methods for creating a ChromeOS Recovery USB:</p>
<h3 id="method-1-chromebook-recovery-utility-recommended" tabindex="-1"><a class="header-anchor" href="#method-1-chromebook-recovery-utility-recommended"><span>Method 1: Chromebook Recovery Utility (Recommended)</span></a></h3>
<p><strong>Supported Platforms:</strong> Windows, ChromeOS, macOS</p>
<ol>
<li>
<p>Download the <a href="https://chrome.google.com/webstore/detail/chromebook-recovery-utili/pocpnlppkickgojjlmhdmidojbmbodfm" target="_blank" rel="noopener noreferrer">Chromebook Recovery Utility</a> from the Chrome Web Store</p>
</li>
<li>
<p>Launch the app and follow the on-screen instructions</p>
</li>
<li>
<p>If you can't find your device automatically:</p>
<ul>
<li>Select &quot;Select a model from a list&quot;</li>
<li>Choose your manufacturer and model</li>
</ul>
</li>
<li>
<p>Select the USB drive you want to use</p>
</li>
<li>
<p>Wait for the process to complete</p>
</li>
</ol>
<h3 id="method-2-manual-recovery-image-flash" tabindex="-1"><a class="header-anchor" href="#method-2-manual-recovery-image-flash"><span>Method 2: Manual Recovery Image Flash</span></a></h3>
<p><strong>Use this if:</strong> The Recovery Utility fails, or you're running Linux</p>
<h4 id="step-1-download-the-recovery-image" tabindex="-1"><a class="header-anchor" href="#step-1-download-the-recovery-image"><span>Step 1: Download the Recovery Image</span></a></h4>
<ol>
<li>
<p>Go to <a href="https://cros.tech/" target="_blank" rel="noopener noreferrer">cros.tech</a> and find your device</p>
</li>
<li>
<p>Click on the &quot;Recovery Images&quot; dropdown and select the latest image</p>
</li>
<li>
<p>Download and extract the <code v-pre>.bin</code> file from the archive</p>
</li>
</ol>
<h4 id="step-2-flash-the-recovery-image" tabindex="-1"><a class="header-anchor" href="#step-2-flash-the-recovery-image"><span>Step 2: Flash the Recovery Image</span></a></h4>
<p><strong>Windows:</strong></p>
<p>Use <RouteLink to="/docs/support/bootableusb.html#flashing-using-rufus-windows">Rufus</RouteLink> to flash the recovery <code v-pre>.bin</code> file to USB. <strong>Important:</strong> Flash in &quot;dd&quot; mode, not &quot;ISO&quot; mode.</p>
<p><strong>macOS/ChromeOS/Linux:</strong></p>
<p>Use <RouteLink to="/docs/support/bootableusb.html#flashing-with-dd-linux-macos">dd</RouteLink> to flash the recovery <code v-pre>.bin</code> file to USB.</p>
<p>Example:</p>
<div class="language-bash line-numbers-mode" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line"><span class="token function">sudo</span> <span class="token function">dd</span> <span class="token assign-left variable">if</span><span class="token operator">=</span>chromeos_recovery_image.bin <span class="token assign-left variable">of</span><span class="token operator">=</span>/dev/sdX <span class="token assign-left variable">bs</span><span class="token operator">=</span>4M <span class="token assign-left variable">status</span><span class="token operator">=</span>progress</span>
<span class="line"><span class="token function">sync</span></span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div></div></div><p>Replace <code v-pre>/dev/sdX</code> with your USB device path (use <code v-pre>lsblk</code> to identify).</p>
<h2 id="part-2-booting-from-the-recovery-usb" tabindex="-1"><a class="header-anchor" href="#part-2-booting-from-the-recovery-usb"><span>Part 2: Booting from the Recovery USB</span></a></h2>
<p>Once your recovery USB is ready:</p>
<h3 id="step-1-enter-recovery-mode" tabindex="-1"><a class="header-anchor" href="#step-1-enter-recovery-mode"><span>Step 1: Enter Recovery Mode</span></a></h3>
<ol>
<li>
<p>Power off your device completely</p>
</li>
<li>
<p>Boot to <RouteLink to="/docs/boot-modes/recovery.html">Recovery Mode</RouteLink>:</p>
<ul>
<li>Press and hold <strong>ESC + Refresh (F3)</strong>, then press <strong>Power</strong></li>
<li>Release all keys when you see the recovery screen</li>
</ul>
</li>
</ol>
<h3 id="step-2-start-the-recovery-process" tabindex="-1"><a class="header-anchor" href="#step-2-start-the-recovery-process"><span>Step 2: Start the Recovery Process</span></a></h3>
<ol>
<li>
<p>When prompted with &quot;Chrome OS is missing or damaged&quot;, insert your recovery USB</p>
</li>
<li>
<p>The recovery process will start automatically</p>
</li>
<li>
<p>Wait for the process to complete (typically 5-15 minutes)</p>
</li>
<li>
<p>Your device will reboot automatically when finished</p>
</li>
</ol>
<h3 id="step-3-exit-developer-mode-if-applicable" tabindex="-1"><a class="header-anchor" href="#step-3-exit-developer-mode-if-applicable"><span>Step 3: Exit Developer Mode (If Applicable)</span></a></h3>
<p>If you're returning the device to stock configuration:</p>
<ol>
<li>
<p>On first boot, you'll see the &quot;OS verification is OFF&quot; screen</p>
</li>
<li>
<p>Press <strong>SPACEBAR</strong> to re-enable OS verification</p>
</li>
<li>
<p>Press <strong>ENTER</strong> to confirm</p>
</li>
<li>
<p>The device will powerwash and reboot to stock ChromeOS</p>
</li>
</ol>
<h2 id="troubleshooting" tabindex="-1"><a class="header-anchor" href="#troubleshooting"><span>Troubleshooting</span></a></h2>
<h3 id="recovery-process-fails" tabindex="-1"><a class="header-anchor" href="#recovery-process-fails"><span>Recovery Process Fails</span></a></h3>
<p><strong>If you disabled firmware write protection via battery disconnect:</strong></p>
<ul>
<li>You'll need to disconnect the battery again for recovery to complete successfully</li>
<li>This is due to how the stock firmware handles WP state</li>
</ul>
<p><strong>Recovery logs are created automatically:</strong></p>
<ul>
<li>The recovery USB creates a small (~10MB) Linux-formatted partition</li>
<li>Recovery logs are stored here and can help diagnose failures</li>
<li>Mount this partition on a Linux system to access the logs</li>
</ul>
<h3 id="device-not-recognizing-usb" tabindex="-1"><a class="header-anchor" href="#device-not-recognizing-usb"><span>Device Not Recognizing USB</span></a></h3>
<ol>
<li>Try a different USB port (avoid USB hubs)</li>
<li>Recreate the recovery USB using a different USB drive</li>
<li>Verify you downloaded the correct recovery image for your device</li>
</ol>
<h3 id="recovery-image-won-t-flash" tabindex="-1"><a class="header-anchor" href="#recovery-image-won-t-flash"><span>Recovery Image Won't Flash</span></a></h3>
<ul>
<li>Ensure your USB drive is at least 8GB</li>
<li>Try a different USB drive (some drives have compatibility issues)</li>
<li>On Linux/macOS, ensure you have proper permissions (<code v-pre>sudo</code>)</li>
</ul>
<h2 id="after-recovery" tabindex="-1"><a class="header-anchor" href="#after-recovery"><span>After Recovery</span></a></h2>
<p>Once recovery completes:</p>
<ol>
<li>
<p><strong>Stock firmware restored?</strong> You're done! Your device is back to factory state.</p>
</li>
<li>
<p><strong>Want to reinstall Linux?</strong></p>
<ul>
<li>Re-enable Developer Mode</li>
<li>Run the <RouteLink to="/docs/fwscript.html">Firmware Utility Script</RouteLink> again</li>
<li>Follow the <RouteLink to="/docs/getting-started.html">Getting Started guide</RouteLink></li>
</ul>
</li>
</ol>
<h2 id="related-documentation" tabindex="-1"><a class="header-anchor" href="#related-documentation"><span>Related Documentation</span></a></h2>
<ul>
<li><strong><RouteLink to="/docs/reverting/flashing-stock.html">Flashing Stock Firmware</RouteLink></strong> - Restore stock firmware before recovery</li>
<li><strong><RouteLink to="/docs/boot-modes/recovery.html">Recovery Mode</RouteLink></strong> - Understanding Recovery Mode</li>
<li><strong><RouteLink to="/docs/boot-modes/">Boot Modes</RouteLink></strong> - Developer Mode and other boot states</li>
<li><strong><RouteLink to="/docs/support/bootableusb.html">Making a Bootable USB</RouteLink></strong> - For Linux/Windows installation media</li>
</ul>
</div></template>
<script>
export const _pageData = JSON.parse("{\"path\":\"/docs/reverting/recovery-usb.html\",\"title\":\"ChromeOS Recovery USB\",\"lang\":\"en-US\",\"frontmatter\":{\"prev\":\"flashing-stock\",\"next\":false},\"git\":{\"updatedTime\":1764108211000,\"contributors\":[{\"name\":\"Matt DeVillier\",\"username\":\"\",\"email\":\"matt.devillier@gmail.com\",\"commits\":1}],\"changelog\":[{\"hash\":\"3b9581612ed009e8df1eb8a5e95af2d55961706f\",\"time\":1764108211000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: consolidate ChromeOS recovery documentation\"}]},\"filePathRelative\":\"docs/reverting/recovery-usb.md\"}")
export default { name: "recovery-usb.md" }
</script>